import { gql, DocumentNode } from '@apollo/client';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Icon } from '../../../../blocks/icons';
import {
	InstallmentStatus,
	InvoiceEditApplyTemplateInvoiceFragment,
	InvoiceStatus,
	PaymentProcessor,
} from '#types';
import styles from './InvoiceEditApplyTemplate.module.scss';
import {
	InvoiceEditApplyTemplateModal,
	InvoiceEditApplyTemplateModalProps,
} from './InvoiceEditApplyTemplateModal';
import classNames from 'classnames';
import { Button } from '../../../Button';

const invoiceEditApplyTemplateInvoiceFragment = gql`
	fragment invoiceEditApplyTemplateInvoice on Invoice {
		id
		status
		paymentInstallments {
			id
			status
			processor
			scheduledPaymentDate
		}
		...invoiceEditApplyTemplateModalInvoice
	}
	${ InvoiceEditApplyTemplateModal.fragments
		.invoiceEditApplyTemplateModalInvoiceFragment }
`;

export type InvoiceEditApplyTemplateProps = {
	className?: string;
	invoiceExists: boolean;
	invoice?: Omit<InvoiceEditApplyTemplateInvoiceFragment, 'id'> & {
		id?: string;
	};
} & Pick<
InvoiceEditApplyTemplateModalProps,
'invoiceTemplate' | 'setInvoiceTemplate'
>;

type InvoiceEditApplyTemplatePropsWithFragment =
	React.FC<InvoiceEditApplyTemplateProps> & {
		fragments: {
			invoiceEditApplyTemplateInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditApplyTemplate: InvoiceEditApplyTemplatePropsWithFragment =
	( {
		className,
		invoice,
		invoiceExists,
		invoiceTemplate,
		setInvoiceTemplate,
	} ) => {
		const [ open, setOpen ] = useState( false );

		/* only show the link/button if the following are true:
    - the invoice hasn’t been created yet
    - OR the invoice has a draft or sent status
    	- AND the invoice does NOT have any pending payments
      - AND the invoice does NOT have any offline payments
      - AND auto payments are NOT scheduled for the invoice
		And using invoiceExists so we don't have to wait for the API and context, so
		we don't have to temporarily show the thing and then hide it when the API
		gets back to us */
		const isVisible =
			!invoiceExists ||
			( invoiceExists &&
				invoice &&
				[ InvoiceStatus.Draft, InvoiceStatus.Sent ].includes( invoice.status ) &&
				invoice.paymentInstallments?.every(
					( paymentInstallment ) =>
						paymentInstallment.status &&
						![ InstallmentStatus.Pending, InstallmentStatus.Refunding ].includes(
							paymentInstallment.status
						) &&
						paymentInstallment.processor !== PaymentProcessor.Offline &&
						!paymentInstallment.scheduledPaymentDate
				) );

		if ( !isVisible ) {
			return null;
		}

		return (
			<Box className={ classNames( styles.container, className ) }>
				{ invoiceTemplate ? (
					<Box className={ styles.applied }>
						<Typography>Template applied</Typography>
						<IconButton onClick={ () => setOpen( true ) }>
							<Icon type='pencil' className={ styles.icon } />
						</IconButton>
					</Box>
				) : (
					<Button
						variant='text'
						onClick={ () => setOpen( true ) }
						data-cy='apply-a-template'
					>
						Apply a template
					</Button>
				) }
				<InvoiceEditApplyTemplateModal
					open={ open }
					onClose={ () => setOpen( false ) }
					invoiceTemplate={ invoiceTemplate }
					setInvoiceTemplate={ setInvoiceTemplate }
				/>
			</Box>
		);
	};

InvoiceEditApplyTemplate.fragments = {
	invoiceEditApplyTemplateInvoiceFragment,
};
